/* ================================
   header.js — stabilized version
   ================================ */

/* BasicHeader remains functionally the same,
   but we keep its structure for compatibility. */
class BasicHeader extends HTMLElement {
  constructor() { super(); }

  get headerSection() { return document.querySelector('.header-section'); }
  get headerNavigation() { return this.querySelector('.header__bottom'); }
  get navigationToggleButton() { return this.querySelector('.toggle-navigation-button'); }
  get enableTransparent() { return this.dataset.enableTransparent === 'true'; }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }
    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));
    if (this.enableTransparent) this.headerSection.classList.add('header-transparent');
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;
    headerGroups.forEach((section) => { totalHeight += section.offsetHeight; });
    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);
    return totalHeight;
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      const offsetNavigationHeight = Math.round(this.headerNavigation.offsetHeight);
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-navigation-height', `${offsetNavigationHeight - 1}px`);
      this.calculateHeaderGroupHeight();
    });
  }
}
customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    this.navigationManuallyToggled = false;
    this.currentScrollTop = 0;
    this.lastScrollPos = 0;
    this.scrollThreshold = 200;
    this.scrollDirection = 'none';
    this.scrollDistance = 0;

    // rAF throttle flag
    this._scrollTicking = false;

    // media query for the theme’s lg breakpoint
    this.mqDesktop = window.matchMedia('(min-width: 1024px)');
  }

  get isAlwaysSticky() { return this.dataset.stickyType === 'always'; }
  get collapseOnScroll() { return this.dataset.collapseOnScroll === 'true'; }

  connectedCallback() {
    super.connectedCallback();

    this.firstScrollTop = window.scrollY;
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.initStickyHeader();

    // Toggle button: bind once
    if (this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle.bind(this));
    }

    // Keep bounds fresh on resize/section reloads
    window.addEventListener('resize', this._recalcBounds, { passive: true });
    document.addEventListener('shopify:section:load', this._recalcBounds, { passive: true });
  }

  _recalcBounds = () => {
    this.headerBounds = this.headerSection.getBoundingClientRect();
    this.firstScrollTop = window.scrollY;
  };

  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  handleNavigationToggle = (event) => {
    event.preventDefault();
    this.navigationManuallyToggled = true;

    // The desktop-only toggle should not be touched on mobile
    const isDesktop = this.mqDesktop.matches;

    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Only add/remove is-show on the toggle when it’s *actually visible*
    if (isDesktop && this.navigationToggleButton) {
      this.navigationToggleButton.classList.toggle(this.classes.show, isHidden);
      this.navigationToggleButton.setAttribute('aria-expanded', isHidden);
    }

    // Hide/show nav area
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);

    // Body state for collapsed nav (desktop only)
    if (isDesktop) {
      setTimeout(() => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      }, isHidden ? 0 : 250);
    }

    // Reset guard after a short delay
    setTimeout(() => { this.navigationManuallyToggled = false; }, 800);
  };

  handleScroll() {
    const scrollTop = window.scrollY;

    if (this._scrollTicking) return;
    this._scrollTicking = true;

    requestAnimationFrame(() => {
      this._onScrollRAF(scrollTop);
      this._scrollTicking = false;
    });
  }

  _onScrollRAF(scrollTop) {
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    this._updateScrollMetrics(scrollTop);

    const isScrolledPastHeader = scrollTop > headerBoundsTop;

    if (isScrolledPastHeader) {
      this._handleScrolledPastHeader(scrollTop, headerBoundsBottom);
    } else {
      this._handleScrolledBeforeHeader();
    }

    this.currentScrollTop = scrollTop;
  }

  _updateScrollMetrics(scrollTop) {
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  _handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    const isDesktop = this.mqDesktop.matches;
    this.headerSection.classList.add(this.classes.headerScrolled);

    // Collapse-on-scroll only applies on desktop where the toggle exists visually
    if (this.collapseOnScroll && isDesktop) {
      if (this.navigationToggleButton) {
        this.navigationToggleButton.classList.add(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      }
      if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    }

    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  _handleScrolledBeforeHeader() {
    const isDesktop = this.mqDesktop.matches;
    this.headerSection.classList.remove(this.classes.headerScrolled);

    if (this.collapseOnScroll) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide, this.classes.show);
        if (isDesktop && this.navigationToggleButton) {
          this.navigationToggleButton.setAttribute('aria-expanded', false);
        }
      }
      // Only touch the desktop-only toggle on desktop
      if (isDesktop && this.navigationToggleButton) {
        this.navigationToggleButton.classList.remove(this.classes.show);
      }
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

/* The remaining classes (DetailsDropdown, DetailsMega, MenuDrawerDetails,
   MenuDrawer, MenuProductList, MenuSidebar) remain unchanged from your file. */

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = { handleAfterHide: 'menu:handleAfterHide', handleAfterShow: 'menu:handleAfterShow' };
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');

    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => { if (event.target === this.summaryElement) this.open = true; });
      this.summaryElement.addEventListener('focusout', (event) => { if (!this.contentElement.contains(event.relatedTarget)) this.open = false; });
    }

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);

    this.addEventListener('mouseenter', (event) => {
      if (this.hoverLeaveTimer) { clearTimeout(this.hoverLeaveTimer); this.hoverLeaveTimer = null; }
      this.hoverEnterTimer = setTimeout(() => { this.detectHover({ type: 'mouseenter' }); }, 100);
    });

    this.addEventListener('mouseleave', (event) => {
      if (this.hoverEnterTimer) { clearTimeout(this.hoverEnterTimer); this.hoverEnterTimer = null; }
      this.hoverLeaveTimer = setTimeout(() => { this.detectHover({ type: 'mouseleave' }); }, 150);
    });
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;
      if (this.isConnected) this.transition(value);
      else { if (value) this.setAttribute('open', ''); else this.removeAttribute('open'); }
    }
  }
  get open() { return this._open; }
  get trigger() { return FoxTheme.config.isTouch ? 'click' : (this.getAttribute('trigger') || 'click'); }
  get level() { return this.hasAttribute('level') ? this.getAttribute('level') : 'top'; }

  handleSummaryClick(event) {
    event.preventDefault();
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => { this.contentElement.setAttribute('open', ''); }, 100);
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);
      await this.showWithTransition();
      const MenuProductList = this.querySelector('menu-product-list');
      if (MenuProductList && typeof MenuProductList === 'object') MenuProductList.calcNavButtonsPosition();
      this.needsReverse();
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);
      await this.hideWithTransition();
      if (!this.open) this.removeAttribute('open');
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1], delay: FoxTheme.config.motionReduced ? 0 : 0.1,
    });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(this.contentElement.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1],
    }).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.3, easing: [0.7, 0, 0.2, 1],
    });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(this.contentElement.firstElementChild, { transform: `translateY(${translateY})` }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1],
    }).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;
    if (!isClickInside && !isClickOnDetailsDropdown) this.open = false;
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) targetMenu.open = false;
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.open = false;
  }

  detectHover(event) {
    if (this.trigger === 'hover') this.open = event.type === 'mouseenter';
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) this.contentElement.classList.add('needs-reverse');
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => { this.open = true; });
      this.addEventListener('shopify:block:deselect', () => { this.open = false; });
    }
  }
  async showWithTransition() {
    return FoxTheme.Motion.animate(this.contentElement.firstElementChild, {
      visibility: 'visible', transform: ['translateY(-100%)', 'translateY(0)'],
    }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.5, easing: [0.39, 0.575, 0.565, 1.0],
    }).finished;
  }
  async hideWithTransition() {
    return FoxTheme.Motion.animate(this.contentElement.firstElementChild, {
      visibility: 'hidden', transform: 'translateY(-100%)',
    }, {
      duration: FoxTheme.config.motionReduced ? 0 : 0.5, easing: [0.39, 0.575, 0.565, 1.0],
    }).finished;
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }
  get parent() { return this.closest('[data-parent]'); }
  get summary() { return this.querySelector('summary'); }
  get closeButton() { return this.querySelector('button'); }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }
  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);
    this.closeAnimation();
  }
  closeAnimation() {
    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      const elapsedTime = time - animationStart;
      if (elapsedTime < 400) window.requestAnimationFrame(handleAnimation);
      else this.removeAttribute('open');
    };
    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() { super(); }
  get header() { return this.closest('header'); }
  get requiresBodyAppended() { return false; }

  prepareToShow() {
    super.prepareToShow();
    setTimeout(() => {}, 300);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom)}px`);
    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }
  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => { menu.onCloseButtonClick(); });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }
  get container() { return this.querySelector('.swiper'); }
  get sliderPagination() { return this.querySelector('.swiper-pagination'); }
  get sliderNext() { return this.querySelector('.swiper-button-next'); }
  get sliderPrev() { return this.querySelector('.swiper-button-prev'); }
  get numberOfColumns() { return parseInt(this.dataset.columns); }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: { nextEl: this.sliderNext, prevEl: this.sliderPrev },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();
    const focusableElements = FoxTheme.a11y.getFocusableElements(this);
    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');
        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
customElements.define('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }
  get summarys() { return this.querySelectorAll('summary'); }

  connectedCallback() {
    this.setInitialMinHeight();
    const firstSummary = this.summarys[0];
    if (firstSummary) this.setActiveItem(firstSummary, false);
    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', (e) => e.preventDefault());
    });
    this.setupIntersectionObserver();
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');
    if (container) {
      const parentHeight = container.parentElement.offsetHeight;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) this.updateHeight(); });
    });
    observer.observe(this);
  }

  updateHeight() {
    const activeSummary = this.querySelector('.is-visible');
    if (activeSummary) {
      const container = this.closest('.mega-menu__wrapper');
      const content = activeSummary.nextElementSibling;
      if (container && content) container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');
    if (lastSidenavElem) lastSidenavElem.classList.remove('is-visible');
    summaryElem.classList.add('is-visible');
    if (updateHeight) this.updateHeight();
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.target;
    this.setActiveItem(summaryElem);
  }

  disconnectedCallback() {
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleClick);
    });
  }
}
customElements.define('menu-sidebar', MenuSidebar);

/* Keep mobile search icon visible on <750px>, but don’t touch desktop state */
if (window.innerWidth < 750) {
  const searchIcon = document.querySelector('.search__icon-search');
  if (searchIcon) {
    searchIcon.style.position = 'static';
    searchIcon.style.display = 'inline-flex';
    searchIcon.style.opacity = '1';
    searchIcon.style.visibility = 'visible';
  }
}
